<template>
  <div
    class="route-loader-overlay"
    :class="{ 'route-loader-overlay--content': preserveNavigation, 'route-loader-overlay--page': variant === 'page' }"
    role="status"
    aria-live="polite"
    :aria-label="label"
  >
    <div class="route-loader">
      <div class="route-loader-truck" aria-hidden="true" v-html="mixerTruckMarkup" />
      <p v-if="mixerTruckReady" class="route-loader-label">{{ label }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import mixerTruckMarkup from '@/assets/images/loaders/caminhao-betoneira-loading.svg?raw';

export default defineComponent({
  name: 'LoaderFullScreen',
  setup() {
    return { mixerTruckMarkup };
  },
  data() {
    return {
      mixerTruckReady: false
    };
  },
  mounted() {
    window.requestAnimationFrame(() => {
      this.mixerTruckReady = true;
    });
  },
  props: {
    label: {
      type: String,
      default: 'Carregando dados da obra...'
    },
    preserveNavigation: {
      type: Boolean,
      default: false
    },
    // 'overlay' cobre o conteúdo da aplicação (translúcido, com delay para não
    // piscar em navegação rápida). 'page' é para telas que só têm o loader,
    // como o callback do OIDC: fundo opaco e revelação imediata.
    variant: {
      type: String,
      default: 'overlay'
    }
  }
});
</script>

<style lang="scss">
$leftbar-width: 260px;
$leftbar-width-md: 160px;
$leftbar-width-sm: 70px;

.route-loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 999;
  display: grid;
  place-items: center;
  min-width: 0;
  background: rgba(246, 248, 252, 0.9);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.route-loader-overlay--page {
  background: #f6f8fc;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;

  // Não há conteúdo atrás para piscar, e o #app-boot-loader do index.html acaba
  // de sair: qualquer atraso aqui deixaria a tela vazia no meio do caminho.
  .route-loader {
    animation-delay: 0ms;
  }
}

.route-loader {
  display: flex;
  width: min(230px, calc(100vw - 48px));
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transform: translateY(-3vh);
  animation: route-loader-reveal 160ms ease-out 500ms forwards;
}

@keyframes route-loader-reveal {
  to {
    opacity: 1;
  }
}

.route-loader-truck {
  display: block;
  width: 100%;
  height: auto;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
}

.route-loader-label {
  margin: -18px 0 0;
  color: #12245a;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.035em;
}

html[data-theme='dark'] {
  .route-loader-overlay {
    background: rgba(18, 25, 45, 0.4);
  }

  .route-loader-overlay--page {
    background: #12192d;
  }

  .route-loader {
    position: relative;
    isolation: isolate;

    &::before {
      position: absolute;
      z-index: -1;
      width: 132%;
      height: 118%;
      content: '';
      background: radial-gradient(ellipse at center, rgba(170, 187, 232, 0.28) 0%, rgba(91, 108, 160, 0.16) 42%, transparent 72%);
      filter: blur(10px);
      pointer-events: none;
    }
  }

  .route-loader-label {
    color: #edf1ff;
  }

  .route-loader-truck #cena {
    --traco: #c2d0ff !important;
    --agua1: #7189ca !important;
    --agua2: #a6b5e5 !important;
    --ativo: #f25ba5 !important;
  }
}

.route-loader-overlay--content {
  inset: 70px 0 0 $leftbar-width;

  .route-loader {
    width: min(230px, calc(100vw - #{$leftbar-width} - 48px));
  }
}

html[data-sidenav-size='compact'] {
  .route-loader-overlay--content {
    left: $leftbar-width-md;

    .route-loader {
      width: min(230px, calc(100vw - #{$leftbar-width-md} - 48px));
    }
  }
}

html[data-sidenav-size='condensed'],
html[data-sidenav-size='sm-hover'],
html[data-sidenav-size='sm-hover-active'] {
  .route-loader-overlay--content {
    left: $leftbar-width-sm;

    .route-loader {
      width: min(230px, calc(100vw - #{$leftbar-width-sm} - 48px));
    }
  }
}

html[data-sidenav-size='full'] {
  .route-loader-overlay--content {
    left: 0;

    .route-loader {
      width: min(230px, calc(100vw - 48px));
    }
  }
}

@media (max-width: 767.98px) {
  .route-loader-overlay--content {
    inset: 70px 0 0;
  }

  .route-loader {
    width: min(230px, calc(100vw - 32px));
  }

  .route-loader-label {
    margin-top: -18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .route-loader-overlay {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .route-loader {
    animation-duration: 1ms;
  }
}
</style>
